import React, { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import moment from "moment";
import DateRangePicker from "../date/DateRangePicker";
import FormField from "./FormField";

// #region CONSTANTS
const DEFAULT_FORMAT = "DD/MM/YYYY";
const DEFAULT_CALENDAR_ICON = <span className="fa-light fa-calendar" />;
// #endregion

/** FormFieldDateRangePicker component - combines FormField with DateRangePicker
 * @param {[moment, moment]} value - Controlled value as [startDate, endDate] (moment objects)
 * @param {function} onChange - Callback function called when the range changes (receives dates and dateStrings)
 * @param {string} displayValue - Optional manual override for the displayed input value
 * @param {string} format - Date format (default: 'DD/MM/YYYY')
 * @param {string|moment|Date} disableBefore - Disable dates before this date (accepts presets like "today", moment instance, Date, etc.)
 * @param {string|moment|Date} disableAfter - Disable dates after this date (accepts presets like "today", moment instance, Date, etc.)
 * @param {function} disabledDate - Custom disabledDate function (receives current moment)
 * @param {boolean} singleCalendar - If true, shows a single calendar panel instead of dual (default: false)
 * @param {Array} presets - Preset ranges for quick selection
 * @param {boolean} disabled - Whether the form field is disabled
 * @param {boolean} allowClear - Whether to show a clear button inside the picker popup or input
 * @param {string} popupClassName - Class name for the datepicker popup container
 * @param {object} popupStyle - Inline styling for the datepicker popup container
 * @param {object} pickerProps - Additional raw props passed to the underlying Ant Design RangePicker
 * @param {string} className - Class name for the outer wrapper element
 * @param {object} style - Inline style for the outer wrapper element
 * @param {string|number} width - Custom CSS width for the outer wrapper element
 * @param {React.ReactNode} leftContent - Custom React node to render on the left side of the input (default: calendar icon)
 * @param {boolean} [useRightClearBtn=true] - Whether to show the right clear X button automatically
 * @param {function} [onClearAction] - Optional custom callback when the clear X button is clicked
 * @param {function} [onFormCardChanges] - Callback indicating value changed (typically sets form card dirty status)
 */
const FormFieldDateRangePicker = forwardRef(
    (
        {
            value,
            onChange,
            displayValue,
            format = DEFAULT_FORMAT,
            disableBefore,
            disableAfter,
            disabledDate,
            defaultPickerValue,
            singleCalendar = false,
            presets,
            disabled = false,
            allowClear,
            popupClassName,
            popupStyle,
            pickerProps,
            className,
            style,
            width,
            leftContent = DEFAULT_CALENDAR_ICON,
            useRightClearBtn = true,
            onClearAction,
            onFormCardChanges,
            ...formFieldProps
        },
        ref
    ) => {
        // #region REFS
        const formFieldRef = useRef(null);
        // #endregion

        // #region METHODS
        const handleChange = (dates, dateStrings) => {
            onChange?.(dates, dateStrings);
            onFormCardChanges?.(true);
        };

        const handleClearClick = e => {
            e.stopPropagation();
            if (disabled) return;
            if (onClearAction) {
                onClearAction();
            } else {
                handleChange(null, ["", ""]);
            }
        };

        const isValid = highlight => formFieldRef.current?.isValid?.(highlight) ?? true;

        const getValue = () => value ?? null;

        const setOutsideInvalid = (...args) => formFieldRef.current?.setOutsideInvalid?.(...args);
        // #endregion

        // #region COMPUTED PROPS
        const formattedDisplayValue = useMemo(() => {
            if (displayValue !== undefined) return displayValue;
            if (!value || !value[0] || !value[1]) return "";

            const from = moment(value[0]).format(format);
            const to = moment(value[1]).format(format);
            if (from === "Invalid date" || to === "Invalid date") return "";

            return `${from} - ${to}`;
        }, [displayValue, format, value]);

        const defaultClearBtn = (
            <span
                className={`fa-light fa-xmark w-100 h-100 ${disabled ? "text-inactive" : "text-normal"}`}
                style={{ alignContent: "center", justifyContent: "center", textAlign: "center" }}
                onClick={handleClearClick}
            />
        );

        const effectiveRightContent =
            formFieldProps.rightContent !== undefined
                ? formFieldProps.rightContent
                : useRightClearBtn
                  ? defaultClearBtn
                  : undefined;
        // #endregion

        useImperativeHandle(ref, () => ({
            isValid,
            getValue,
            setOutsideInvalid
        }));

        // #region RENDER
        return (
            <DateRangePicker
                value={value}
                onChange={handleChange}
                format={format}
                disableBefore={disableBefore}
                disableAfter={disableAfter}
                disabledDate={disabledDate}
                defaultPickerValue={defaultPickerValue}
                singleCalendar={singleCalendar}
                presets={presets}
                disabled={disabled}
                allowClear={allowClear}
                popupClassName={popupClassName}
                popupStyle={popupStyle}
                pickerProps={pickerProps}
                className={className}
                style={style}
                width={width}>
                <FormField
                    {...formFieldProps}
                    ref={formFieldRef}
                    type="text"
                    leftContent={leftContent}
                    rightContent={effectiveRightContent}
                    value={formattedDisplayValue}
                />
            </DateRangePicker>
        );
        // #endregion
    }
);

export default FormFieldDateRangePicker;
